import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  Tabs,
  addToast,
} from "@heroui/react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, MessageSquare, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import {
  MarkdownEditor,
  type MarkdownEditorRef,
} from "@/Components/NextBase/LimeMarkdownEditor";
import { useConfirmModal } from "@/Components/NextBase/useConfirmModal";
import { api } from "@/lib/api-client";
import { cn } from "@/utils";
import type { PostWaitlistList } from "@/server-types";

type Entry = PostWaitlistList["response"]["entries"][number];

type Channel = "sms" | "email";

type MessagingChannelType = "sms" | "viber" | "whatsapp";

/**
 * Human-readable label for each messaging channel in the fallback flow.
 * Used to build the dynamic "SMS/Viber/Whatsapp" label on the messaging
 * channel button. Not localised — these are brand names.
 */
const MESSAGING_CHANNEL_LABELS: Record<MessagingChannelType, string> = {
  sms: "SMS",
  viber: "Viber",
  whatsapp: "Whatsapp",
};

/** Sole tag wired into the editor today. Keeping it as a module-level
 *  const so its identity is stable across renders — passing a fresh array
 *  to `MarkdownEditor` on every render would re-instantiate the TipTap
 *  extension and reset cursor state. */
const LINK_TAG_OPTIONS = [{ value: "{link}", label: "Link" }];

/**
 * Tab order — also the swipe-animation orientation reference. Going to a
 * later index counts as `direction = +1` (rightward); to an earlier index
 * counts as `-1`. Tab labels in the UI follow this order left-to-right.
 */
const CHANNEL_ORDER: Record<"sms" | "email", number> = {
  sms: 0,
  email: 1,
};

/**
 * Framer-motion variants for the editor swipe. New content slides in
 * from the side you're moving towards (right tab → enters from right),
 * old content slides out to the opposite side. The translate distance
 * is small (12%) — enough to read as motion, not so large that it
 * overshoots the modal's body padding.
 */
const SWIPE_VARIANTS = {
  enter: (direction: number) => ({
    x: direction > 0 ? "12%" : "-12%",
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? "-12%" : "12%",
    opacity: 0,
  }),
};

const SWIPE_TRANSITION = { duration: 0.18, ease: "easeOut" } as const;

/**
 * Convert the editor's HTML output to plain text suitable for the
 * notify-now endpoint (which expects the same plain-text-with-`{link}`
 * shape the modal sent before we switched to the rich editor — the
 * server's escape/paragraph handling stays unchanged).
 *
 * Rules:
 *  - `<br>` → `\n`
 *  - block-level elements (`<p>`, `<div>`, headings, list items, …) →
 *    `\n\n` after their children
 *  - text nodes are kept verbatim (TipTap escapes user text into entities
 *    on serialize, so `textContent` decodes them back transparently)
 *  - zero-width spaces that TipTap inserts for cursor positioning are
 *    stripped, and runs of 3+ newlines collapse to 2 so we don't pile up
 *    blank paragraphs on serialise → parse round-trips.
 *  - `{link}` tokens survive untouched because `MarkdownEditor.onUpdate`
 *    runs `stripNotificationTagsFromHtml` before reporting the value, so
 *    by the time we see it the pill spans are already plain text again.
 */
function htmlToPlainText(html: string): string {
  if (!html) return "";
  if (typeof window === "undefined" || typeof document === "undefined") {
    // Fallback: strip tags via regex. Only ever hit during SSR; the editor
    // itself requires DOM so this branch is paranoia.
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .trim();
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  const BLOCK_LEVEL_TAGS = new Set([
    "p",
    "div",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "li",
    "blockquote",
    "pre",
  ]);

  const parts: string[] = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? "");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === "br") {
      parts.push("\n");
      return;
    }

    for (const child of Array.from(node.childNodes)) {
      walk(child);
    }

    if (BLOCK_LEVEL_TAGS.has(tag)) {
      parts.push("\n\n");
    }
  };

  walk(container);

  return parts
    .join("")
    .replace(/\u200B/g, "") // TipTap cursor-positioning zero-width spaces
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Lowercase HTML entities for the characters the editor cares about. We
 *  control the source (it's a translation catalogue value, not user-typed),
 *  so this only exists to avoid a translator accidentally introducing an
 *  unescaped `<` or `&` that the browser would interpret as markup. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Convert a translator-authored plain-text template (with `\n\n`
 * paragraph breaks, optional single `\n` soft breaks, and `{link}`
 * tokens) into the HTML shape `MarkdownEditor` expects as `value`.
 *
 * The inverse of `htmlToPlainText`: round-tripping a template through
 * both functions yields the same plain text. We use this so the default
 * messages stay as ONE translatable string per channel in the Crowdin
 * catalogue \u2014 translators see the full message in context instead of
 * disconnected `Hi,` / `Thanks!` fragments \u2014 and only convert to HTML
 * at the moment we hand the template to the editor.
 *
 * `{link}` tokens are NOT substituted here. They're emitted verbatim
 * (and escaped along with everything else, though the braces don't need
 * escaping); the editor's `NotificationTag` extension picks them up at
 * mount time and renders them as the pretty pill.
 */
function plainTextToEditorHtml(text: string): string {
  if (!text) return "";
  return text
    .split(/\n{2,}/)
    .map((para) =>
      para
        .split("\n")
        .map((line) => escapeHtml(line))
        .join("<br>"),
    )
    .map((para) => `<p>${para}</p>`)
    .join("");
}

export function NotifyCustomerModal({
  entry,
  isOpen,
  onClose,
  onSuccess,
}: {
  entry: Entry;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const {
    data: channelsData,
    isLoading: isChannelsLoading,
    processedErrorMessage: channelsErrorMessage,
  } = api.notification.useGetNotificationChannels();

  /**
   * Localized default message bodies per channel. Each channel's catalog
   * entry is a single plain-text string with `\n\n` paragraph breaks and
   * a `{link}` placeholder — translators see one cohesive message per
   * channel in Crowdin (no "Hi," / "Thanks!" floating around as
   * orphaned fragments) and can reorder paragraphs to match the target
   * language's natural flow.
   *
   * `plainTextToEditorHtml` converts each template to the HTML shape the
   * editor expects right before handing it off; the editor's
   * `NotificationTag` extension picks up the `{link}` literal and renders
   * it as the pretty pill. The same plain-text shape is what we'll send
   * back to the server (after the user edits it), so the template
   * round-trips losslessly.
   *
   * Computed inside the component (not at module level) so the `t`
   * macros evaluate under the active locale on each render — a
   * module-level `t` would freeze the value at import time.
   */
  // Local alias for the literal `{link}` token. We pipe it through JS
  // interpolation rather than embedding `{link}` directly inside the `t`
  // template so Lingui treats it as a named placeholder (catalog entry
  // becomes `…{link}…`, translators can move it freely). Embedding the
  // raw braces would otherwise trip ICU MessageFormat at runtime — it'd
  // see `{link}` as an unbound placeholder and either drop it or throw,
  // depending on the formatter's mood.
  const link = "{link}";
  const defaultMessageTemplates: Record<Channel, string> = {
    email: t`Pozdravljeni,

Pravkar se je odprlo mesto, ki ustreza vašim željam na čakalni vrsti. Uporabite spodnjo povezavo, da jo zahtevate, preden to stori nekdo drug:

${link}

Hvala!`,
    sms: t`Pravkar se je odprlo mesto – zahtevajte ga tukaj: ${link}`,
  };

  const defaultMessages: Record<Channel, string> = {
    email: plainTextToEditorHtml(defaultMessageTemplates.email),
    sms: plainTextToEditorHtml(defaultMessageTemplates.sms),
  };

  // Configured messaging channels (sms/viber/whatsapp) in the client's
  // fallback order. The server's dispatch service walks this same order
  // when the operator picks the messaging channel — the label here shows
  // exactly which legs would fire ("SMS/Viber" or "SMS/Whatsapp" etc.).
  const messagingChannels = useMemo<MessagingChannelType[]>(() => {
    const rows = channelsData?.channels ?? [];
    return rows
      .filter(
        (c): c is typeof c & { notificationType: MessagingChannelType } =>
          c.notificationType === "sms" ||
          c.notificationType === "viber" ||
          c.notificationType === "whatsapp",
      )
      .sort((a, b) => a.fallbackOrder - b.fallbackOrder)
      .map((c) => c.notificationType);
  }, [channelsData]);

  // Channel availability is the AND of two gates:
  //   1. tenant-level — does the client have the infrastructure?
  //      • email: always available (dispatched via global `sendEmails`,
  //        not gated by `ClientNotificationChannel`)
  //      • messaging: needs at least one sms/viber/whatsapp row in the
  //        client's notification channels. While the query is in flight
  //        we optimistically assume "yes" to avoid a flicker, then
  //        collapse if the resolved list is empty.
  //   2. customer-level — can we actually reach THIS customer on it?
  //      • email: customer must have an email address on file
  //      • messaging: customer must have a phone number on file
  //        (`fullPhoneNumber` is the projected gsm+countryCode pair;
  //        null means the customer either has no phone or the operator
  //        lacks visibility permissions — either way, we shouldn't try)
  //
  // Both gates must pass for the channel button to appear. The initial
  // channel selection (below) is the first one that passes — defaulting
  // blindly to "email" would put the operator on a dead channel for
  // phone-only customers.
  const customerHasEmail = useMemo(
    () =>
      typeof entry.customer.email === "string" &&
      entry.customer.email.length > 0,
    [entry.customer.email],
  );
  const customerHasPhone = entry.customer.fullPhoneNumber != null;

  const isEmailAvailable = customerHasEmail;
  const isMessagingAvailable =
    customerHasPhone && (!channelsData || messagingChannels.length > 0);

  const messagingLabel = useMemo(() => {
    if (messagingChannels.length === 0) return MESSAGING_CHANNEL_LABELS.sms;
    return messagingChannels.map((c) => MESSAGING_CHANNEL_LABELS[c]).join("/");
  }, [messagingChannels]);

  // First available channel is the right default — email if reachable,
  // otherwise messaging. If neither is reachable we still pick a value
  // (defaulting to "email" so the state type stays narrow); the picker
  // collapses, the body renders the no-contact-method warning, and the
  // Send button stays disabled.
  const initialChannel: Channel = isEmailAvailable
    ? "email"
    : isMessagingAvailable
      ? "sms"
      : "email";

  const [channel, setChannel] = useState<Channel>(initialChannel);
  const [message, setMessage] = useState<string>(
    defaultMessages[initialChannel],
  );
  // When set, the operator clicked a different tab while their message
  // body diverged from the current channel's default. The confirm modal
  // opens to let them lose-or-keep their edits; the switch only commits
  // if they confirm.
  const [pendingChannel, setPendingChannel] = useState<Channel | null>(null);
  // Direction the editor swipes during a channel change: +1 = the new
  // channel sits to the right of the old (slide goes right-to-left), -1
  // = the new sits to the left. Drives the framer-motion variants below.
  const [swipeDirection, setSwipeDirection] = useState(0);

  // Ref to the TipTap editor instance so the "Insert link" chip can run
  // commands against it. Forwarded by `MarkdownEditor` via
  // `useImperativeHandle`.
  const editorRef = useRef<MarkdownEditorRef | null>(null);

  const { mutateAsync, isPending, processedErrorMessage, reset } =
    api.waitlist.usePostNotifyNow();

  const customerFullName = useMemo(() => {
    const name =
      `${entry.customer.name ?? ""} ${entry.customer.lastName ?? ""}`.trim();
    return name.length > 0 ? name : t`stranko`;
  }, [entry.customer.name, entry.customer.lastName]);

  // Subtitle context line — for group entries we have a concrete service
  // name, for regular entries we surface the first preferred service. This
  // mirrors how the rest of the waitlist UI summarises an entry's target.
  const targetLabel = useMemo(() => {
    if (entry.type === "group") {
      return entry.groupAppointment?.service?.name ?? null;
    }
    return entry.regularServices?.[0]?.name ?? null;
  }, [entry.type, entry.groupAppointment, entry.regularServices]);

  // Plain-text projection of the editor body — used three ways: dirty
  // detection, `{link}` occurrence counting, and the final value sent to
  // the server. Computing once and reusing keeps the three call sites
  // consistent (otherwise dirty-check could disagree with the link-count
  // check on edge whitespace cases).
  const plainTextMessage = useMemo(() => htmlToPlainText(message), [message]);

  const linkCount = useMemo(() => {
    const matches = plainTextMessage.match(/\{link\}/g);
    return matches ? matches.length : 0;
  }, [plainTextMessage]);
  const hasLink = linkCount >= 1;
  const hasTooManyLinks = linkCount > 1;

  // Compare current editor content against the active channel's default by
  // their plain-text projections. Comparing raw HTML would flicker between
  // "dirty" and "clean" on harmless serialisation differences (whitespace
  // around tags, zero-width spaces, attribute order). Plain-text is the
  // stable surface — and matches what the server eventually receives.
  const isMessageDirty =
    plainTextMessage !== htmlToPlainText(defaultMessages[channel]);

  const handleClose = () => {
    // Reset transient mutation error so it doesn't flash again when the
    // operator re-opens the modal from the dropdown.
    reset();
    setChannel(initialChannel);
    setMessage(defaultMessages[initialChannel]);
    setPendingChannel(null);
    setSwipeDirection(0);
    onClose();
  };

  /**
   * Commit a channel switch. The framer-motion `AnimatePresence` around
   * the editor below uses `swipeDirection` to decide which side to slide
   * the old content out to and the new content in from — so we update
   * direction BEFORE the key change, in the same render. No setTimeout
   * chain, no fade-pause sentinel state: the animation system handles
   * the transition entirely.
   *
   * `pendingChannel` is intentionally untouched here; the confirm modal
   * clears it via `onOpenChange` when the dialog closes.
   */
  const switchChannel = (next: Channel) => {
    setSwipeDirection(
      Math.sign(CHANNEL_ORDER[next] - CHANNEL_ORDER[channel]) || 1,
    );
    setChannel(next);
    setMessage(defaultMessages[next]);
  };

  const requestChannelSwitch = (next: Channel) => {
    if (next === channel) return;
    if (isMessageDirty) {
      // Open the confirm modal; the actual switch happens in its onClick.
      setPendingChannel(next);
      return;
    }
    switchChannel(next);
  };

  // Auto-correct the active channel when the channels query resolves with
  // the currently-selected channel no longer available — most commonly
  // when the optimistic "messaging is available during load" assumption
  // flips to false after we see the empty list (or, mirror-image, when
  // the customer has no email and we'd defaulted to sms based on stale
  // state). Silent switch: no confirm prompt, because this typically
  // fires before any user interaction and the operator can't usefully
  // "stay" on an unreachable channel. If neither channel is available,
  // we leave the state pointing at the original pick — the picker
  // collapses and the dead-end warning banner explains the situation.
  //
  // The state updates here are intentionally direct (not via
  // `switchChannel`) so we skip the swap animation: there's no user
  // interaction to acknowledge, just an availability fact catching up
  // with the optimistic initial state, and a flash of fade-out → swap →
  // fade-in on mount feels broken. The lint rules below would otherwise
  // misinterpret "set channel + message from a useEffect" as derived
  // state, which it isn't — `channel` is the operator's real selection
  // and only gets corrected when their original target became
  // unreachable.
  /* eslint-disable react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-event-handler */
  useEffect(() => {
    if (channel === "sms" && !isMessagingAvailable && isEmailAvailable) {
      setChannel("email");
      setMessage(defaultMessages.email);
    } else if (
      channel === "email" &&
      !isEmailAvailable &&
      isMessagingAvailable
    ) {
      setChannel("sms");
      setMessage(defaultMessages.sms);
    }
    // `defaultMessages` is recomputed every render but its content only
    // changes on locale flips, which already trigger a re-render and a
    // fresh effect run. Excluding it keeps the effect from re-firing on
    // every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, isEmailAvailable, isMessagingAvailable]);
  /* eslint-enable react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-event-handler */

  // Confirm dialog for losing operator edits on channel switch. Controlled
  // by `pendingChannel` — opens when an operator attempts a switch with
  // dirty content, closes on either button. The pending target is what
  // we'd switch to on confirm; cancel just clears the pending state.
  const confirmSwitchModal = useConfirmModal({
    title: t`Želite zamenjati kanal?`,
    content: (
      <p className="text-sm">
        {t`Vaše sporočilo bo nadomeščeno s privzeto vsebino za nov kanal in vaše spremembe bodo izgubljene.`}
      </p>
    ),
    isOpen: pendingChannel != null,
    onOpenChange: (open) => {
      if (!open) setPendingChannel(null);
    },
    confirmProps: {
      label: t`Nadomesti s privzeto`,
      onClick: async () => {
        if (pendingChannel) switchChannel(pendingChannel);
      },
    },
    cancelProps: {
      label: t`Obdrži sporočilo`,
      onClick: () => setPendingChannel(null),
    },
  });

  const handleInsertLink = () => {
    // Defensive: don't insert a second link even if the button is briefly
    // mis-enabled (e.g., paste-rule race where state hasn't refreshed
    // yet). The TipTap command itself doesn't enforce uniqueness — the
    // single-`{link}` invariant lives here.
    if (hasLink) return;
    editorRef.current?.commands.insertNotificationTag("{link}");
  };

  const handleSend = async () => {
    try {
      await mutateAsync({
        waitlistId: entry.waitlistId,
        body: {
          channel,
          // Empty / whitespace-only → fall back to the server's built-in
          // template instead of sending a blank notification.
          customMessage: plainTextMessage.trim() || undefined,
        },
      });
      addToast({ title: t`Stranka obveščena`, color: "success" });
      onSuccess?.();
      handleClose();
    } catch (err) {
      Sentry.addBreadcrumb({
        category: "ui.action",
        message: "notify-customer send error",
        level: "error",
        data: { error: err instanceof Error ? err.message : String(err) },
      });
      // Inline LimeAlert below surfaces the server-localized reason.
    }
  };

  const channelOptions: {
    key: Channel;
    label: string;
    icon: React.ReactNode;
    available: boolean;
  }[] = [
    {
      key: "sms",
      label: messagingLabel,
      icon: <MessageSquare size={18} />,
      available:
        isMessagingAvailable &&
        entry.customer.fullPhoneNumber != null &&
        entry.customer.fullPhoneNumber.length > 0,
    },
    {
      key: "email",
      label: t`E-pošta`,
      icon: <Mail size={18} />,
      available:
        isEmailAvailable &&
        entry.customer.email != null &&
        entry.customer.email.length > 0,
    },
  ];

  return (
    <>
      <LimeModal
        isOpen={isOpen}
        onOpenChange={(open) => !open && handleClose()}
        size="lg"
        placement="center"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span>{t`Pošlji obvestilo`}</span>
            <span className="text-default-500 text-sm font-normal">
              {targetLabel
                ? t`Obvesti ${customerFullName}, da je prosto mesto za ${targetLabel}.`
                : t`Obvesti ${customerFullName}, da je prosto mesto.`}
            </span>
          </ModalHeader>
          <ModalBody className="gap-5">
            {channelsErrorMessage && (
              <LimeAlert message={channelsErrorMessage} />
            )}

            {/* Surface the dead-end case up front. Hitting Send when no
                channel is reachable would already no-op (Send is disabled
                below), but operators deserve a reason — otherwise the
                disabled state feels broken.

                Only two reachable subcases here: either the customer has
                neither contact field, OR they have a phone but the tenant
                hasn't configured any messaging channel. A customer with
                an email always has the email button available, so the
                "has-email" branch never hits this banner. */}
            {channelOptions.every((o) => !o.available) && (
              <LimeAlert
                color="warning"
                message={
                  !customerHasPhone
                    ? t`Tej stranki ne moremo poslati obvestila — nima vnešene e-pošte ali telefonske številke.`
                    : t`Tej stranki ne moremo poslati obvestila — nima vnešene e-pošte in v vašem računu ni nastavljenih sporočilnih kanalov.`
                }
              />
            )}

            {/* Picker is only meaningful when the operator has at least
                two reachable channels to choose between. With one (or
                zero) we skip rendering it entirely — the active channel
                is implicit, and the dead-end banner above covers the
                zero case. Unavailable channels are hidden rather than
                shown disabled: a greyed-out tab next to an enabled one
                gives no actionable signal once the operator clocks the
                reason. */}
            {(() => {
              const visibleChannels = channelOptions.filter((o) => o.available);
              // if (visibleChannels.length < 2) return null;
              // Tab order in the rendered list matches `CHANNEL_ORDER` so
              // the swipe direction calculated in `switchChannel` matches
              // the visual layout. HeroUI's `Tabs` honours the order of
              // its child `Tab` nodes.
              const orderedChannels = [...visibleChannels].sort(
                (a, b) => CHANNEL_ORDER[a.key] - CHANNEL_ORDER[b.key],
              );
              return (
                <Tabs
                  aria-label={t`Kanal`}
                  selectedKey={channel}
                  onSelectionChange={(key) =>
                    requestChannelSwitch(key as Channel)
                  }
                  variant="solid"
                  fullWidth
                  isDisabled={isChannelsLoading}
                >
                  {orderedChannels.map((opt) => (
                    <Tab
                      key={opt.key}
                      title={
                        <div className="flex items-center gap-2">
                          {opt.icon}
                          <span>{opt.label}</span>
                        </div>
                      }
                    />
                  ))}
                </Tabs>
              );
            })()}

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{t`Sporočilo`}</span>
                <button
                  type="button"
                  onClick={handleInsertLink}
                  disabled={hasLink || isPending}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors",
                    "focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-2",
                    hasLink || isPending
                      ? "border-default-200 text-default-400 cursor-not-allowed"
                      : "border-primary-200 bg-primary-50 text-primary-600 hover:bg-primary-100",
                  )}
                  title={
                    hasLink
                      ? t`Povezava je že vstavljena.`
                      : t`Vstavi spremenljivko Link na trenutno mesto kazalca.`
                  }
                >
                  <Plus size={12} />
                  <span className="font-mono">Link</span>
                </button>
              </div>

              {/* Swipe-style channel transition. `mode="popLayout"` runs
                  the exit and enter animations concurrently and pops the
                  outgoing element out of the layout flow — the incoming
                  editor takes the slot immediately, so the swap reads as
                  one continuous swipe instead of "old leaves, gap, new
                  arrives". Combined with the direction-aware variants
                  below, the old editor slides out one side while the new
                  one slides in from the other. `key={channel}` re-mounts
                  the TipTap instance on each swap, which is fine: the
                  channel switch always replaces content with a new
                  default, so cursor state is moot. `overflow-x-hidden`
                  clips translated content during animation so it never
                  bleeds past the modal body's padding. */}
              <div className="relative overflow-x-hidden">
                <AnimatePresence
                  mode="popLayout"
                  custom={swipeDirection}
                  initial={false}
                >
                  <motion.div
                    key={channel}
                    custom={swipeDirection}
                    variants={SWIPE_VARIANTS}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={SWIPE_TRANSITION}
                  >
                    <MarkdownEditor
                      ref={editorRef}
                      value={message}
                      onChange={setMessage}
                      tagOptions={LINK_TAG_OPTIONS}
                      // Hide every rich-text affordance — bold/italic/lists/etc.
                      // wouldn't survive the round-trip to SMS anyway, and the
                      // email renderer wraps the body in its own chrome that
                      // doesn't depend on inline formatting. Undo/Redo stay so
                      // the (always-rendered) toolbar isn't an empty divider.
                      excludeActions={[
                        "bold",
                        "italic",
                        "strike",
                        "link",
                        "image",
                        "unordered-list",
                        "ordered-list",
                        "horizontal-rule",
                        "paragraph",
                      ]}
                      isDisabled={isPending}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              <p className="text-default-500 text-xs">
                {t`Spremenljivka Link predstavlja povezavo za rezervacijo. Če je ne vključite, bo povezava dodana na koncu sporočila.`}
              </p>

              {hasTooManyLinks && (
                <LimeAlert
                  color="warning"
                  message={t`Sporočilo lahko vsebuje le eno povezavo. Odstranite dodatne, preden pošljete.`}
                />
              )}
            </div>

            <LimeAlert message={processedErrorMessage} />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={handleClose}
              isDisabled={isPending}
            >
              {t`Prekliči`}
            </Button>
            <Button
              color="primary"
              onPress={handleSend}
              isLoading={isPending}
              isDisabled={
                channelOptions.every((o) => !o.available) ||
                channelOptions.length === 0 ||
                hasTooManyLinks
              }
            >
              {t`Pošlji obvestilo`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </LimeModal>
      {confirmSwitchModal.modal}
    </>
  );
}
